import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, ReactNode, lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { UserAuthProvider } from './hooks/useUserAuth'
import { EnvironmentProvider } from './hooks/useEnvironment'
import { ErrorToastProvider } from './components/ErrorToast'
import { ReauthProvider } from './components/ReauthProvider'

// Eager load landing page and legal pages (always instantly available)
import LandingPage from './pages/landing/LandingPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'

// Lazy load other pages for code splitting
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const AccountManage = lazy(() => import('./pages/account/AccountManage'))

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-indigo-900 flex items-center justify-center">
    <div className="animate-pulse text-indigo-300">Loading...</div>
  </div>
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const APP_ALLOWLIST_PATHS = [
  '/',
  '/landing',
  '/admin',
  '/account/manage',
  '/account/reset',
  '/account/delete',
  '/privacy',
  '/terms',
]

const enableFullApp = import.meta.env.VITE_ENABLE_FULL_APP === 'true'

function LandingGate({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const isAllowlisted = APP_ALLOWLIST_PATHS.some((path) =>
      path === '/' ? location.pathname === '/' || location.pathname === '' : location.pathname.startsWith(path),
    )

    if (enableFullApp || isAuthenticated || isAllowlisted) {
      return
    }

    if (location.pathname !== '/') {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, location.pathname, navigate])

  return children
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorToastProvider>
        <EnvironmentProvider>
          <AuthProvider>
            <UserAuthProvider>
              <ReauthProvider>
                <Router>
                  <LandingGate>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/landing" element={<LandingPage />} />
                        <Route path="/admin/*" element={<AdminLayout />} />
                        <Route path="/account/manage" element={<AccountManage />} />
                        <Route path="/account/reset" element={<AccountManage defaultAction="reset" />} />
                        <Route path="/account/delete" element={<AccountManage defaultAction="delete" />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                      </Routes>
                    </Suspense>
                  </LandingGate>
                </Router>
              </ReauthProvider>
            </UserAuthProvider>
          </AuthProvider>
        </EnvironmentProvider>
      </ErrorToastProvider>
    </QueryClientProvider>
  )
}

export default App
